import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useUser } from "@clerk/clerk-react";
import {
  Box, Typography, Paper, Stack, Button, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, Chip, IconButton,
  useTheme, Select, MenuItem, FormControl, InputLabel, Alert, Avatar, alpha, Divider, Grid, Tooltip, Slide
} from "@mui/material";
import {
  AddOutlined as AddIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  ChairAltOutlined as ScreenIcon,
  VisibilityOutlined as ViewIcon,
  GridViewOutlined as LayoutIcon,
  SettingsOutlined as ConfigIcon,
  InfoOutlined as InfoIcon,
  DesignServices as DesignIcon
} from "@mui/icons-material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { getPartnerScreens, addPartnerScreen, deletePartnerScreen } from "../../store/slices/screenSlice";
import { useSocket } from "../../contexts/SocketContext";
import { toast } from "react-toastify";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PartnerScreens = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const { user: clerkUser } = useUser();
  const { socket: socketInstance } = useSocket();
  const { screens, loading } = useSelector(state => state.screen);
  const { partner } = useSelector(state => state.partner);
  const isDark = mode === 'dark';

  const colors = useMemo(() => ({
    primary: "#666CFF",
    success: "#72E128",
    warning: "#FDB528",
    error: "#FF4D49",
    info: "#26C6F9",
    textPrimary: isDark ? "rgba(231, 227, 252, 0.87)" : "rgba(58, 53, 65, 0.87)",
    textSecondary: isDark ? "rgba(231, 227, 252, 0.6)" : "rgba(58, 53, 65, 0.6)",
    background: isDark ? "#282A42" : "#ffffff",
    paper: isDark ? "#30334E" : "#ffffff",
    divider: isDark ? "rgba(231, 227, 252, 0.12)" : "rgba(58, 53, 65, 0.12)",
  }), [isDark]);

  const [addDialog, setAddDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [newScreen, setNewScreen] = useState({
    screenNumber: 1, name: '', capacity: 100, screenType: '2D',
    soundSystem: 'Dolby Digital', projectionType: 'Digital', isActive: true
  });

  const fetchScreens = React.useCallback(() => {
    const theaterId = localStorage.getItem('activeTheaterId');
    if (clerkUser?.id) dispatch(getPartnerScreens({ userId: clerkUser.id, theaterId }));
  }, [dispatch, clerkUser?.id]);

  useEffect(() => {
    fetchScreens();
  }, [fetchScreens]);

  useEffect(() => {
    if (socketInstance) {
      const events = ["theater-updated", "screen-updated", "seat-layout-updated", "partner-updated"];
      events.forEach(e => socketInstance.on(e, fetchScreens));
      return () => events.forEach(e => socketInstance.off(e, fetchScreens));
    }
  }, [socketInstance, fetchScreens]);

  const handleOpenAddDialog = () => {
    const plan = partner?.currentPlan || 'Starter';
    const limit = plan === 'Starter' ? 1 : plan === 'Growth' ? 4 : 999;
    if (screens.length >= limit) {
      return toast.error(`Your ${plan} plan allows up to ${limit} screen${limit > 1 ? 's' : ''}. Upgrade mapped!`);
    }
    setNewScreen(p => ({ ...p, screenNumber: (screens.length || 0) + 1 }));
    setAddDialog(true);
  };

  const handleAddScreen = async () => {
    if (!newScreen.name) return toast.error("Screen name is required");
    try {
      const theaterId = localStorage.getItem('activeTheaterId');
      await dispatch(addPartnerScreen({ userId: clerkUser.id, screenData: { ...newScreen, theaterId } })).unwrap();
      toast.success("Screen added!");
      setAddDialog(false);
      setNewScreen(p => ({ ...p, screenNumber: (screens.length || 0) + 2, name: '' }));
    } catch (err) {
      toast.error(err.message || "Failed to add screen");
    }
  };

  const handleDeleteScreen = async () => {
    if (!deleteConfirm) return;
    try {
      await dispatch(deletePartnerScreen({ userId: clerkUser.id, screenId: deleteConfirm })).unwrap();
      toast.success("Screen decommissioned");
      setDeleteConfirm(null);
    } catch (err) {
      toast.error(err.message || "Failed to decommission");
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Node Identifier',
      Cell: ({ row, renderedCellValue }) => (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: alpha(colors.primary, 0.1), color: colors.primary, borderRadius: '8px', width: 36, height: 36 }}>
            <ScreenIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: colors.textPrimary }}>{renderedCellValue}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>Sequence: #{row.original.screenNumber}</Typography>
          </Box>
        </Stack>
      ),
    },
    {
      accessorKey: 'capacity',
      header: 'Load Units',
      Cell: ({ renderedCellValue }) => (
        <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>{renderedCellValue} Seats</Typography>
      ),
    },
    {
      accessorKey: 'screenType',
      header: 'Visual Protocol',
      Cell: ({ renderedCellValue }) => (
        <Chip
          label={renderedCellValue}
          size="small"
          sx={{ fontWeight: 900, fontSize: '0.62rem', bgcolor: alpha(colors.primary, 0.1), color: colors.primary, height: 20 }}
        />
      ),
    },
    {
      accessorKey: 'soundSystem',
      header: 'Audio Matrix',
      Cell: ({ renderedCellValue }) => (
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary }}>{renderedCellValue}</Typography>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Node State',
      Cell: ({ renderedCellValue }) => (
        <Chip
          label={renderedCellValue !== false ? 'OPERATIONAL' : 'OFFLINE'}
          size="small"
          sx={{
            height: 20, fontSize: '0.62rem', fontWeight: 950,
            bgcolor: renderedCellValue !== false ? alpha(colors.success, 0.1) : alpha(colors.error, 0.1),
            color: renderedCellValue !== false ? colors.success : colors.error
          }}
        />
      ),
    },
    {
      id: 'actions',
      header: 'Operations',
      Cell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Architect Layout">
            <IconButton
              size="small"
              component={Link}
              to={`/partner/screens/${row.original._id}/layout`}
              disabled={partner?.kycStatus !== 'APPROVED'}
              sx={{ color: colors.primary, bgcolor: alpha(colors.primary, 0.1), borderRadius: '8px' }}
            >
              <DesignIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Decommission Node">
            <IconButton
              size="small"
              onClick={() => setDeleteConfirm(row.original._id)}
              disabled={partner?.kycStatus !== 'APPROVED'}
              sx={{ color: colors.error, bgcolor: alpha(colors.error, 0.1), borderRadius: '8px' }}
            >
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ], [colors, partner?.kycStatus]);

  const table = useMaterialReactTable({
    columns,
    data: screens,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: true,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: '12px',
        border: `1px solid ${colors.divider}`,
        bgcolor: colors.paper,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 800,
        fontSize: '0.7rem',
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        py: 2,
        bgcolor: alpha(colors.textPrimary, 0.02),
      },
    },
    muiTableBodyCellProps: {
      sx: {
        py: 1.5,
        color: colors.textPrimary,
        borderBottom: `1px solid ${colors.divider}`,
      },
    },
  });

  return (
    <Box sx={{ pb: 5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: colors.textPrimary, letterSpacing: '-0.02em', mb: 0.5 }}>
            Auditorium Architecture
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
            Configure and orchestrate viewing nodes for optimal seat distribution
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
          disabled={partner?.kycStatus !== 'APPROVED'}
          sx={{ borderRadius: '10px', px: 3, py: 1, fontWeight: 700, bgcolor: colors.primary, '&:hover': { bgcolor: alpha(colors.primary, 0.8) } }}
        >
          Initialize Node
        </Button>
      </Box>

      {partner?.kycStatus !== 'APPROVED' && (
        <Alert icon={<InfoIcon />} severity="info" sx={{ mb: 4, borderRadius: '12px', fontWeight: 700, bgcolor: alpha(colors.info, 0.05), color: colors.info, border: `1px solid ${alpha(colors.info, 0.1)}` }}>
          PROTOCOL LOCKED: Verified partner credentials required for architectural changes.
        </Alert>
      )}

      <MaterialReactTable table={table} />

      <Dialog
        open={addDialog}
        onClose={() => setAddDialog(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: '28px',
            bgcolor: isDark ? alpha('#0f1014', 0.95) : '#fff',
            backgroundImage: 'none',
            border: `1px solid ${alpha(colors.textPrimary, 0.08)}`,
            backdropFilter: 'blur(30px)',
            boxShadow: isDark ? '0 50px 100px rgba(0,0,0,0.8)' : '0 20px 60px rgba(0,0,0,0.08)'
          }
        }}
      >
        <DialogTitle sx={{ p: 4, pb: 2 }}>
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(colors.primary, 0.1), color: colors.primary, width: 56, height: 56, borderRadius: '18px', border: `1px solid ${alpha(colors.primary, 0.2)}` }}>
              <ScreenIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 950, fontSize: '1.5rem', color: colors.textPrimary, letterSpacing: '-0.03em', lineHeight: 1.2 }}>
                Initialize Node
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: colors.primary, letterSpacing: '0.12em', textTransform: 'uppercase', mt: 0.5 }}>
                Hardware Specification Protocol
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 4, pt: 1 }}>
          <Typography sx={{ color: colors.textSecondary, mb: 4, fontSize: '0.9rem', fontWeight: 500, lineHeight: 1.6, opacity: 0.8 }}>
            Define the operational parameters for this viewing auditorium. Accurate capacity load is required for seat registry synchronization.
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label="Node Designation (Name)"
                placeholder="e.g. IMAX Alpha"
                value={newScreen.name}
                onChange={e => setNewScreen({ ...newScreen, name: e.target.value })}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '16px',
                    bgcolor: alpha(colors.textPrimary, 0.02),
                    '& fieldset': { borderColor: alpha(colors.textPrimary, 0.1) },
                  },
                  '& .MuiInputLabel-root': { fontWeight: 700, fontSize: '0.85rem' }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Capacity"
                type="number"
                value={newScreen.capacity}
                onChange={e => setNewScreen({ ...newScreen, capacity: parseInt(e.target.value) || 0 })}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '16px',
                    bgcolor: alpha(colors.textPrimary, 0.02),
                    '& fieldset': { borderColor: alpha(colors.textPrimary, 0.1) },
                  },
                  '& .MuiInputLabel-root': { fontWeight: 700, fontSize: '0.85rem' }
                }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Visual Protocol</InputLabel>
                <Select
                  value={newScreen.screenType}
                  onChange={e => setNewScreen({ ...newScreen, screenType: e.target.value })}
                  label="Visual Protocol"
                  sx={{
                    borderRadius: '16px',
                    bgcolor: alpha(colors.textPrimary, 0.02),
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(colors.textPrimary, 0.1) },
                  }}
                >
                  <MenuItem value="2D">Standard 2D</MenuItem>
                  <MenuItem value="3D">IMAX 3D</MenuItem>
                  <MenuItem value="4DX">4DX Immersion</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Audio Matrix</InputLabel>
                <Select
                  value={newScreen.soundSystem}
                  onChange={e => setNewScreen({ ...newScreen, soundSystem: e.target.value })}
                  label="Audio Matrix"
                  sx={{
                    borderRadius: '16px',
                    bgcolor: alpha(colors.textPrimary, 0.02),
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(colors.textPrimary, 0.1) },
                  }}
                >
                  <MenuItem value="Dolby Digital">Dolby Digital</MenuItem>
                  <MenuItem value="Dolby Atmos">Dolby Atmos [7.1.4]</MenuItem>
                  <MenuItem value="DTS:X">DTS:X Protocol</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 4, pt: 1, justifyContent: 'space-between' }}>
          <Button
            onClick={() => setAddDialog(false)}
            sx={{ fontWeight: 800, color: colors.textSecondary, textTransform: 'none', px: 3, borderRadius: '12px' }}
          >
            Abort Handshake
          </Button>
          <Button
            variant="contained"
            onClick={handleAddScreen}
            sx={{
              borderRadius: '16px',
              px: 5,
              py: 2,
              fontWeight: 900,
              textTransform: 'none',
              bgcolor: colors.primary,
              boxShadow: `0 15px 30px ${alpha(colors.primary, 0.3)}`,
              '&:hover': { bgcolor: alpha(colors.primary, 0.8), transform: 'translateY(-2px)' },
              transition: '0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Sync & Register Node
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            bgcolor: isDark ? alpha('#1c1c21', 0.9) : '#fff',
            backgroundImage: 'none',
            border: `1px solid ${alpha(colors.textPrimary, 0.1)}`,
            backdropFilter: 'blur(20px)',
            boxShadow: isDark ? '0 40px 100px rgba(0,0,0,0.5)' : '0 20px 60px rgba(0,0,0,0.06)'
          }
        }}
      >
        <DialogTitle sx={{ p: 4, pb: 0 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(colors.error, 0.12), color: colors.error, width: 48, height: 48, borderRadius: '14px' }}>
              <DeleteIcon />
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: '1.25rem', color: colors.textPrimary, letterSpacing: '-0.02em' }}>
                Decommission Node
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.error, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Irreversible Action
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Typography sx={{ color: colors.textSecondary, fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.6 }}>
            Are you certain you want to decommission this viewing node? All associated showtime registries and seat configurations will be purged from the network.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 4, pt: 0 }}>
          <Button onClick={() => setDeleteConfirm(null)} sx={{ fontWeight: 800, color: colors.textSecondary, textTransform: 'none' }}>
            Abort Action
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteScreen}
            sx={{
              borderRadius: '14px',
              px: 3,
              fontWeight: 900,
              textTransform: 'none',
              bgcolor: colors.error,
              boxShadow: `0 12px 24px ${alpha(colors.error, 0.3)}`,
              '&:hover': { bgcolor: alpha(colors.error, 0.8) }
            }}
          >
            Confirm Purge
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerScreens;
